import { type FC, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CandlestickSeries,
  ColorType,
  CrosshairMode,
  HistogramSeries,
  createChart,
  type CandlestickData,
  type HistogramData,
  type IChartApi,
  type ISeriesApi,
  type Time,
} from 'lightweight-charts';
import { landingHeroConfig } from '../config/landingHero';

const stats = [
  { value: '7x', label: 'Bonded max leverage' },
  { value: '2-5%', label: 'Tiered flat fee' },
  { value: '24h', label: 'Auto-close window' },
  { value: '30%', label: 'Creator revenue share' },
];

type LandingMarket = (typeof landingHeroConfig.featuredMarkets)[number];
type MarketSymbol = LandingMarket['symbol'];
type ChartInterval = (typeof landingHeroConfig.chart.intervals)[number];

type ChartFeed = {
  candles: CandlestickData<Time>[];
  volumes: HistogramData<Time>[];
  source: 'Binance spot' | 'Preview feed';
};

type ChartFeedCache = Record<string, ChartFeed>;

const defaultChartInterval = landingHeroConfig.chart.intervals[0];

const getChartKey = (market: LandingMarket, interval: ChartInterval) => `${market.symbol}:${interval.value}`;

const formatChartPrice = (value: number) => {
  if (value >= 1000) return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (value >= 1) return value.toFixed(2);
  if (value >= 0.01) return value.toFixed(4);
  return value.toPrecision(3);
};

const formatChartChange = (value: number) => `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;

const marketSeed = (symbol: string) => symbol.split('').reduce((sum, letter) => sum + letter.charCodeAt(0), 0);

const generatePreviewFeed = (market: LandingMarket, interval: ChartInterval): ChartFeed => {
  const candles: CandlestickData<Time>[] = [];
  const volumes: HistogramData<Time>[] = [];
  const now = Math.floor(Date.now() / 1000);
  const start = now - interval.seconds * 96;
  const seed = marketSeed(market.symbol);
  let price = Number(market.fallbackPrice);

  for (let index = 0; index < 96; index += 1) {
    const time = (start + index * interval.seconds) as Time;
    const open = price;
    const drift = Math.sin((index + seed) / 7) * 0.006 + (index - 48) * 0.0001;
    const close = Math.max(open * (1 + drift), market.fallbackPrice * 0.72);
    const spread = Math.abs(close - open) + market.fallbackPrice * (0.004 + (index % 4) * 0.0008);
    const high = Math.max(open, close) + spread * 0.5;
    const low = Math.max(Math.min(open, close) - spread * 0.45, market.fallbackPrice * 0.5);

    candles.push({ time, open, high, low, close });
    volumes.push({
      time,
      value: 60000 + Math.abs(Math.sin(index / 5 + seed)) * 140000,
      color: close >= open ? 'rgba(14, 203, 129, 0.28)' : 'rgba(246, 70, 93, 0.28)',
    });
    price = close;
  }

  return { candles, volumes, source: 'Preview feed' };
};

const createPreviewFeedCache = () => {
  const feeds: ChartFeedCache = {};
  for (const market of landingHeroConfig.featuredMarkets) {
    for (const interval of landingHeroConfig.chart.intervals) {
      feeds[getChartKey(market, interval)] = generatePreviewFeed(market, interval);
    }
  }
  return feeds;
};

const fetchChartFeed = async (
  market: LandingMarket,
  interval: ChartInterval,
  signal: AbortSignal,
): Promise<ChartFeed> => {
  const url = `https://api.binance.com/api/v3/klines?symbol=${market.binanceSymbol}&interval=${interval.value}&limit=96`;
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error('Chart feed unavailable');

  const rows = await response.json();
  if (!Array.isArray(rows)) throw new Error('Unexpected chart feed response');

  const candles: CandlestickData<Time>[] = rows.map((row) => ({
    time: Math.floor(Number(row[0]) / 1000) as Time,
    open: Number(row[1]),
    high: Number(row[2]),
    low: Number(row[3]),
    close: Number(row[4]),
  }));

  const volumes: HistogramData<Time>[] = rows.map((row) => {
    const open = Number(row[1]);
    const close = Number(row[4]);
    return {
      time: Math.floor(Number(row[0]) / 1000) as Time,
      value: Number(row[5]),
      color: close >= open ? 'rgba(14, 203, 129, 0.28)' : 'rgba(246, 70, 93, 0.28)',
    };
  });

  return { candles, volumes, source: 'Binance spot' };
};

const getMarketSummary = (market: LandingMarket, feed?: ChartFeed) => {
  if (!feed || feed.source !== 'Binance spot') return { price: market.price, move: market.move };
  const first = feed.candles[0]?.close;
  const last = feed.candles.at(-1)?.close;
  if (!first || !last) return { price: market.price, move: market.move };
  return {
    price: `$${formatChartPrice(last)}`,
    move: formatChartChange(((last - first) / first) * 100),
  };
};

const flow = [
  ['Collateral', 'Trader deposits SOL and chooses a listed token.'],
  ['Protocol fill', 'Front supplies the rest of the spot position.'],
  ['Jupiter swap', 'The trade executes on-chain, not as a synthetic perp.'],
  ['Risk engine', 'Position is monitored against tier thresholds.'],
  ['Close', 'Trader exits, or the system auto-closes at risk limits.'],
];

const tiers = [
  { name: 'Bonded', leverage: '7x', fee: '2%', threshold: '-15%', note: 'Graduated liquidity and lower execution risk.' },
  { name: 'Rising', leverage: '5x', fee: '3%', threshold: '-12%', note: 'Growing market cap with moderate liquidity.' },
  { name: 'Degen', leverage: '3x', fee: '5%', threshold: '-10%', note: 'Newer tokens with tighter position caps.' },
];

const scenarios = [
  { title: 'Token rallies 20%', pnl: '+0.70 SOL', note: 'Profit after the 30% FRONT lock.', tone: 'positive' },
  { title: 'Token slips 8%', pnl: '-0.20 SOL', note: 'Loss remains inside trader collateral.', tone: 'negative' },
  { title: 'Token breaks risk limit', pnl: 'Auto-close', note: 'Protocol capital is recovered first.', tone: 'warning' },
];

const faqs = [
  ['Is this a perp exchange?', 'No. Front opens real spot positions through Jupiter, then manages risk around the borrowed protocol capital.'],
  ['Why do token creators care?', 'Creators can route Pump.fun rewards to Front, get listed, and earn a share of leverage trading fees.'],
  ['Where does risk show up?', 'Every token tier has a max leverage, flat fee, threshold, and position-size cap. The UI should make those visible before opening a trade.'],
];

const TierBadge: FC<{ children: string }> = ({ children }) => (
  <span className={`landing-tier landing-tier-${children.toLowerCase()}`}>{children}</span>
);

const HeroChart: FC<{
  feed: ChartFeed;
  interval: ChartInterval;
  market: LandingMarket;
  onIntervalChange: (interval: ChartInterval) => void;
}> = ({ feed, interval, market, onIntervalChange }) => {
  const chartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const volumeSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const summary = getMarketSummary(market, feed);

  useEffect(() => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const chart = createChart(containerRef.current, {
      width: rect.width,
      height: rect.height || 260,
      layout: {
        background: { type: ColorType.Solid, color: '#0b0e11' },
        textColor: '#848e9c',
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 11,
      },
      grid: {
        vertLines: { color: '#181a20' },
        horzLines: { color: '#181a20' },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: 'rgba(240, 185, 11, 0.28)', labelBackgroundColor: '#f0b90b' },
        horzLine: { color: 'rgba(240, 185, 11, 0.28)', labelBackgroundColor: '#f0b90b' },
      },
      rightPriceScale: {
        borderColor: '#2b3139',
        scaleMargins: { top: 0.12, bottom: 0.24 },
      },
      timeScale: {
        borderColor: '#2b3139',
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 4,
      },
      handleScroll: { vertTouchDrag: false },
    });

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#0ecb81',
      downColor: '#f6465d',
      borderVisible: false,
      wickUpColor: '#0ecb81',
      wickDownColor: '#f6465d',
      priceFormat: { type: 'custom', formatter: formatChartPrice },
    });

    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: '',
    });
    volumeSeries.priceScale().applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;
    volumeSeriesRef.current = volumeSeries;

    const resizeObserver = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      chart.applyOptions({ width, height });
    });
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      chart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      volumeSeriesRef.current = null;
    };
  }, []);

  useEffect(() => {
    candleSeriesRef.current?.setData(feed.candles);
    volumeSeriesRef.current?.setData(feed.volumes);
    chartRef.current?.timeScale().fitContent();
  }, [feed]);

  return (
    <div className="hero-chart-module">
      <div className="chart-toolbar">
        <div>
          <div className="chart-title">{market.pairLabel}</div>
          <div className="chart-meta">
            <strong>{summary.price}</strong>
            <span className={summary.move.startsWith('+') ? 'text-green' : 'text-red'}>{summary.move}</span>
            <span>{feed.source === 'Binance spot' ? landingHeroConfig.chart.source : 'Preloaded preview'}</span>
            <span>{landingHeroConfig.chart.network}</span>
          </div>
        </div>
        <div className="chart-controls" aria-label="Chart timeframe">
          {landingHeroConfig.chart.intervals.map((item) => (
            <button
              className={item.value === interval.value ? 'chart-control-active' : ''}
              key={item.value}
              type="button"
              onClick={() => onIntervalChange(item)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
      <div className="chart-canvas" ref={containerRef} />
    </div>
  );
};

const HeroTerminal: FC = () => {
  const [selectedSymbol, setSelectedSymbol] = useState<MarketSymbol>(landingHeroConfig.featuredMarkets[0].symbol);
  const [chartInterval, setChartInterval] = useState<ChartInterval>(defaultChartInterval);
  const [chartFeeds, setChartFeeds] = useState<ChartFeedCache>(() => createPreviewFeedCache());
  const activeMarket = landingHeroConfig.featuredMarkets.find((market) => market.symbol === selectedSymbol)
    ?? landingHeroConfig.featuredMarkets[0];
  const activeFeed = chartFeeds[getChartKey(activeMarket, chartInterval)]
    ?? generatePreviewFeed(activeMarket, chartInterval);

  useEffect(() => {
    const controller = new AbortController();

    const preloadFeeds = async () => {
      const entries = await Promise.all(
        landingHeroConfig.featuredMarkets.flatMap((market) =>
          landingHeroConfig.chart.intervals.map(async (interval) => {
            const key = getChartKey(market, interval);
            try {
              return [key, await fetchChartFeed(market, interval, controller.signal)] as const;
            } catch {
              return [key, generatePreviewFeed(market, interval)] as const;
            }
          }),
        ),
      );

      if (!controller.signal.aborted) setChartFeeds(Object.fromEntries(entries));
    };

    preloadFeeds();
    return () => controller.abort();
  }, []);

  return (
    <div className="landing-terminal" aria-label="Front trading terminal preview">
      <div className="terminal-top">
        <span>FRONT PRO TERMINAL</span>
        <span>SOLANA SPOT</span>
      </div>
      <div className="terminal-grid">
        <div className="terminal-chart">
          <HeroChart
            feed={activeFeed}
            interval={chartInterval}
            market={activeMarket}
            onIntervalChange={setChartInterval}
          />
        </div>
        <div className="terminal-ticket">
          <div className="ticket-pair">
            <span>{activeMarket.symbol}</span>
            <strong>{activeMarket.leverage}</strong>
          </div>
          <dl>
            <div><dt>Collateral</dt><dd>{activeMarket.collateral}</dd></div>
            <div><dt>Protocol fill</dt><dd>{activeMarket.protocolFill}</dd></div>
            <div><dt>Position</dt><dd>{activeMarket.position}</dd></div>
            <div><dt>Flat fee</dt><dd>{activeMarket.fee}</dd></div>
          </dl>
          <Link to="/explore" className="terminal-action">Open app</Link>
        </div>
      </div>
      <div className="landing-market-table" aria-label="Preloaded Solana market charts">
        {landingHeroConfig.featuredMarkets.map((row) => {
          const rowSummary = getMarketSummary(row, chartFeeds[getChartKey(row, defaultChartInterval)]);
          return (
            <button
              aria-label={`Show ${row.pairLabel} chart`}
              aria-pressed={row.symbol === selectedSymbol}
              className={`terminal-row${row.symbol === selectedSymbol ? ' terminal-row-active' : ''}`}
              key={row.symbol}
              type="button"
              onClick={() => setSelectedSymbol(row.symbol)}
            >
              <span className="token-symbol">{row.symbol}</span>
              <span>{rowSummary.price}</span>
              <span className={rowSummary.move.startsWith('+') ? 'text-green' : 'text-red'}>{rowSummary.move}</span>
              <span>{row.leverage}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const LeverageModule: FC = () => {
  const [collateral, setCollateral] = useState(0.5);
  const [leverage, setLeverage] = useState(5);
  const position = collateral * leverage;
  const fill = collateral * (leverage - 1);
  const feePct = leverage >= 7 ? 2 : leverage >= 5 ? 3 : 5;
  const fee = position * (feePct / 100);
  const threshold = leverage >= 7 ? -15 : leverage >= 5 ? -12 : -10;

  return (
    <div className="landing-leverage-panel">
      <div className="section-kicker">Position preview</div>
      <div className="range-row">
        <label htmlFor="collateral-range">Collateral</label>
        <strong>{collateral.toFixed(2)} SOL</strong>
      </div>
      <input
        id="collateral-range"
        type="range"
        min="0.1"
        max="5"
        step="0.1"
        value={collateral}
        onChange={(event) => setCollateral(Number(event.target.value))}
      />
      <div className="range-row">
        <label htmlFor="leverage-range">Leverage</label>
        <strong>{leverage}x</strong>
      </div>
      <input
        id="leverage-range"
        type="range"
        min="2"
        max="7"
        step="1"
        value={leverage}
        onChange={(event) => setLeverage(Number(event.target.value))}
      />
      <div className="fill-bar" aria-label="Collateral and protocol fill split">
        <span style={{ width: `${100 / leverage}%` }}>You</span>
        <span style={{ width: `${100 - 100 / leverage}%` }}>Protocol</span>
      </div>
      <div className="metric-grid">
        <div><span>Position</span><strong>{position.toFixed(2)} SOL</strong></div>
        <div><span>Protocol fill</span><strong>{fill.toFixed(2)} SOL</strong></div>
        <div><span>Fee</span><strong>{fee.toFixed(3)} SOL</strong></div>
        <div><span>Risk threshold</span><strong>{threshold}%</strong></div>
      </div>
    </div>
  );
};

export const Landing: FC = () => {
  return (
    <main className="landing-page">
      <nav className="landing-nav" aria-label="Primary navigation">
        <Link to="/" className="landing-brand">
          <img src="/front-logo.png" alt="" width="24" height="24" />
          <span>FRONT</span>
        </Link>
        <div className="landing-links">
          <a href="#product">Product</a>
          <a href="#risk">Risk</a>
          <a href="#creators">Creators</a>
          <Link to="/docs">Docs</Link>
          <a href="https://github.com/FrontDotFun/front" target="_blank" rel="noreferrer">GitHub</a>
        </div>
        <Link to="/explore" className="landing-nav-action">Launch app</Link>
      </nav>

      <section className="landing-hero">
        <div className="hero-copy">
          <p className="section-kicker">Solana market terminal</p>
          <h1>
            Trade listed
            <br className="hero-mobile-break" /> Pump.fun markets
            <br className="hero-mobile-break" /> with exchange-grade
            <br className="hero-mobile-break" /> leverage.
          </h1>
          <p>
            Front gives Solana traders a familiar spot-exchange workflow for
            high-velocity memecoin markets: collateral, protocol fill, clear
            liquidation rules, and real Jupiter execution.
          </p>
          <div className="hero-actions">
            <Link to="/explore" className="landing-button landing-button-primary">Start trading</Link>
            <Link to="/docs" className="landing-button landing-button-secondary">Read protocol docs</Link>
          </div>
        </div>
        <HeroTerminal />
      </section>

      <section className="landing-stats" aria-label="Protocol highlights">
        {stats.map((stat) => (
          <div key={stat.label}>
            <strong>{stat.value}</strong>
            <span>{stat.label}</span>
          </div>
        ))}
      </section>

      <section id="product" className="landing-section two-column">
        <div>
          <p className="section-kicker">How it works</p>
          <h2>Make the trade feel as legible as a major exchange ticket.</h2>
          <p>
            The interface now leads with market depth, position math,
            liquidation context, and token-tier rules before the user reaches
            the order button.
          </p>
        </div>
        <div className="flow-list">
          {flow.map(([title, body]) => (
            <article key={title}>
              <h3>{title}</h3>
              <p>{body}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="risk" className="landing-section two-column landing-section-tight">
        <div>
          <p className="section-kicker">Leverage calculator</p>
          <h2>Show fill, fee, and risk before the order is sent.</h2>
          <p>
            Leverage feels credible when the user can see collateral,
            protocol capital, fees, and the liquidation line in one compact
            ticket.
          </p>
        </div>
        <LeverageModule />
      </section>

      <section className="landing-section">
        <div className="section-heading">
          <p className="section-kicker">Risk tiers</p>
          <h2>List risk the way traders expect to read it.</h2>
        </div>
        <div className="tier-grid">
          {tiers.map((tier) => (
            <article key={tier.name}>
              <TierBadge>{tier.name}</TierBadge>
              <dl>
                <div><dt>Max leverage</dt><dd>{tier.leverage}</dd></div>
                <div><dt>Flat fee</dt><dd>{tier.fee}</dd></div>
                <div><dt>Exit threshold</dt><dd>{tier.threshold}</dd></div>
              </dl>
              <p>{tier.note}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="landing-section scenario-section">
        <div className="section-heading">
          <p className="section-kicker">Position outcomes</p>
          <h2>Keep upside visible and downside explicit.</h2>
        </div>
        <div className="scenario-grid">
          {scenarios.map((item) => (
            <article className={`scenario-card scenario-${item.tone}`} key={item.title}>
              <h3>{item.title}</h3>
              <strong>{item.pnl}</strong>
              <p>{item.note}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="creators" className="landing-section creator-band">
        <div>
          <p className="section-kicker">For token creators</p>
          <h2>List by routing creator rewards.</h2>
          <p>
            No application funnel. No marketing waitlist. Creator rewards fund
            the pool, Front lists the token, and the creator earns fee share
            when traders open positions.
          </p>
        </div>
        <Link to="/list" className="landing-button landing-button-primary">List a token</Link>
      </section>

      <section className="landing-section faq-section">
        <div className="section-heading">
          <p className="section-kicker">FAQ</p>
          <h2>Common questions</h2>
        </div>
        <div className="faq-list">
          {faqs.map(([question, answer]) => (
            <details key={question}>
              <summary>{question}</summary>
              <p>{answer}</p>
            </details>
          ))}
        </div>
      </section>

      <footer className="landing-footer">
        <span>Front Protocol</span>
        <div>
          <Link to="/docs">Docs</Link>
          <Link to="/stats">Stats</Link>
          <Link to="/list">List token</Link>
          <a href="https://x.com/FrontDotFun" target="_blank" rel="noreferrer">X</a>
        </div>
      </footer>
    </main>
  );
};
